import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import {
    DataLoaderUpliftHelper,
    IDataLoaderUpliftHelperOptions,
} from 'src/app/services/data-loader/data-loader-uplift.helper';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    generateTableFullPath,
    ICompiledSQLTokens,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { BiVizDashboardAbTestsProgressDurationSqlMap } from '../sql-maps/biviz-dashboard-abtests-progress-duration-sql-map';

export interface RevenueARPUDataLoaderFactoryOptions
    extends
        ICreatedInstanceId,
        IGcpProjectId,
        IQueryCacheService,
        IDataLoaderUpliftHelperOptions {
    /**
     * Pass callback to retrieve max day no for the query.
     * If not provided, the default max day no will be used.
     * @param context - query context
     * @returns Must return a string with integer value or 'unlimited'
     */
    maxDayNoGetter?: (context: IQueryContext) => string;
}

export class AbTestsProgressSessionDataLoaderFactory extends Factory<
    DataLoader,
    RevenueARPUDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'AbTestsProgressSessionDataLoaderFactory';

    /** This function returns DataLoader instance for analytics vs store reports differences */
    getInstance(options: RevenueARPUDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                console.log(
                    'AbTestsProgressSessionDataLoaderFactory',
                    queryContext
                );

                // ignore columns that are not supported in this query
                const ignoredContextColumnIds = [
                    'ACTIVITY_DATE',
                    'ACTIVITY_APP_VERSION',
                ];

                const bqTableSqlMap =
                    BiVizDashboardAbTestsProgressDurationSqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    bqTableSqlMap.sqlMap.columns
                );

                const dsColumnsStatus = DataLoader.getMissingColumnsStatus(
                    dsColumns.missing
                );

                //filter out ignored columns from dsColumns.present
                dsColumns.present = dsColumns.present.filter(
                    (col) => !ignoredContextColumnIds.includes(col.id)
                );

                let getDefaultMaxDayNo = (context: IQueryContext) => {
                    return 'unlimited';
                };

                const maxDayNo = options.maxDayNoGetter
                    ? options.maxDayNoGetter(queryContext)
                    : getDefaultMaxDayNo(queryContext);

                const maxDayNoSQL: Partial<ICompiledSQLTokens> | undefined =
                    maxDayNo !== 'unlimited'
                        ? {
                              where: [`day_no <= ${maxDayNo}`],
                          }
                        : undefined;

                const {
                    splitSelect,
                    splitGroupBy,
                    where,
                    join,
                    windowRunning,
                } = bqTableSqlMap.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    options,
                    maxDayNoSQL
                );

                const referenceSplitGroup = 'ChaptersTest: ReferenceGroup';

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH base AS
(
  SELECT ${splitSelect}
   ds.*
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      bqTableSqlMap.sqlMap.dataset,
      bqTableSqlMap.sqlMap.tableId
  )}\` ds
  ${join}
  ${where}
)

, results AS
(
  SELECT ${splitGroupBy}
    day_no
    , APPROX_QUANTILES(progress_sum_total, 1000)[OFFSET(500)] AS median_progress_sum_total
    , APPROX_QUANTILES(duration_min, 1000)[OFFSET(1)] AS min_duration_min
    , APPROX_QUANTILES(duration_min, 1000)[OFFSET(50)] AS q05_duration_min
    , APPROX_QUANTILES(duration_min, 1000)[OFFSET(500)] AS median_duration_min
    , AVG(duration_min) AS avg_duration_min
    , APPROX_QUANTILES(duration_min, 1000)[OFFSET(950)] AS q95_duration_min
    , APPROX_QUANTILES(duration_min, 1000)[OFFSET(1000)] AS max_duration_min
    , SUM(APPROX_QUANTILES(duration_min, 1000)[OFFSET(500)]) OVER (${windowRunning}) AS running_median_duration_min
    -- count distinct players who where active
    , COUNT(DISTINCT player_id) AS players
    -- count distinct players that have made progress
    , COUNT(DISTINCT progress_player_id) AS progress_players
  FROM base
  GROUP BY ${splitGroupBy}day_no
  ORDER BY ${splitGroupBy}day_no
)

SELECT *
FROM results
`;
                /*

-- analyze relative uplift of median_progress_sum_total and running_median_duration_min against reference split_group
, reference AS
(
  SELECT
    day_no
    , median_progress_sum_total AS reference_median_progress_sum_total
    , running_median_duration_min AS reference_running_median_duration_min
  FROM results
  WHERE split_group = '${referenceSplitGroup}'
)

SELECT
  results.*
  , reference.reference_median_progress_sum_total
  , reference.reference_running_median_duration_min
  , results.median_progress_sum_total / reference.reference_median_progress_sum_total - 1 AS median_progress_sum_total_uplift
  , results.running_median_duration_min / reference.reference_running_median_duration_min - 1 AS running_median_duration_min_uplift
FROM results
JOIN reference ON results.day_no = reference.day_no
--WHERE results.split_group != '${referenceSplitGroup}'
*/
                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                DataLoaderUpliftHelper.processUpliftMetrics(
                    options,
                    queryContext,
                    data
                );

                this.emitter.emit({
                    data,
                    queryContext,
                    status: dsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage: dsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
